import { Form } from "@/components/ui/form"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { AccountSettingsForm } from "@/types/my-account"
import { MyAccountSchema } from "@/constants/my-account/my-account"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { useSelector, useDispatch } from "react-redux"
import { AppDispatch, RootState } from "@/redux"
import { changePassword } from "@/services/userService"
import { LOCKED_EMAIL_FIELD_MESSAGE } from "@/constants/tooltip/tooltip"
import { UserMessage } from "@/constants/users/user"
import { TextInputField } from "@/components/custom/InputField/TextInputField"
import { getFormProps } from "@/helpers/formHelpers"

export const AccountSettings = () => {
  const dispatch: AppDispatch = useDispatch()
  const { userData, token } = useSelector((state: RootState) => state.user)
  const form = useForm<AccountSettingsForm>({
    resolver: zodResolver(MyAccountSchema.accountSettings),
    defaultValues: {
      email: userData?.email ?? "",
      password: "",
      newPassword: "",
      confirmNewPassword: ""
    }
  })
  const formProps = getFormProps(form, MyAccountSchema.accountSettings)

  useEffect(() => {
    form.clearErrors()
    if (userData) {
      form.setValue('email', userData.email)
    }
  }, [userData, form])

  const onSubmit = async (values: AccountSettingsForm) => {
    if (!token) return
    if (values.newPassword !== values.confirmNewPassword) {
      form.setError('newPassword', { message: 'Passwords do not match.' })
      form.setError('confirmNewPassword', { message: 'Passwords do not match.' })
      return
    }
    const data = {
      oldPassword: values.password,
      newPassword: values.newPassword,
    }
    const resp = await dispatch(changePassword(token, data))
    if (resp.err !== UserMessage.INVALID_PASSWORD) {
      if (resp.data) {
        form.reset({
          email: userData?.email ?? "",
          password: "",
          newPassword: "",
          confirmNewPassword: ""
        })
      }
    }
  }

  const isDisabled = () => {
    return (
      form.getValues('password').length === 0 &&
      form.getValues('newPassword').length === 0 &&
      form.getValues('confirmNewPassword').length === 0
    )
  }

  return (
    <div className="py-10">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-8 flex flex-col items-start"
        >
          <Label className="font-semibold text-secondary mb-6">
            ACCOUNT DETAILS
          </Label>
          <TextInputField
            name="email"
            label="Email Address"
            infoContent={LOCKED_EMAIL_FIELD_MESSAGE}
            error={!!form.formState.errors.email}
            type="email"
            placeholder="john@example.com"
            isDisabled={true}
            {...formProps}
          />
          <TextInputField
            name="password"
            label="Current Password"
            error={!!form.formState.errors.password}
            type="password"
            required={true}
            {...formProps}
          />
          <TextInputField
            name="newPassword"
            label="New Password"
            error={!!form.formState.errors.newPassword}
            type="password"
            required={true}
            {...formProps}
          />
          <TextInputField
            name="confirmNewPassword"
            label="Confirm New Password"
            error={!!form.formState.errors.confirmNewPassword}
            type="password"
            required={true}
            {...formProps}
          />
          <Button
            type="submit"
            className="font-semibold"
            disabled={isDisabled()}
          >
            Update Account Details
          </Button>
        </form>
      </Form>
    </div>
  )
}
